import { useCallback } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  clearAiWidgetMaps,
  selectActiveAiWidgetMap,
  selectAiWidgetMapCount,
  selectAiWidgetMapIndex,
  setAiWidgetMapIndex,
} from "@/reducers/aiWidgetMapSlice"
import AgentMapPanel from "./AgentMapPanel"

type MapWidgetProps = {
  chatJid: string
}

const MapWidget = ({ chatJid }: MapWidgetProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const activeMap = useAppSelector(selectActiveAiWidgetMap(chatJid))
  const count = useAppSelector(selectAiWidgetMapCount(chatJid))
  const index = useAppSelector(selectAiWidgetMapIndex(chatJid))

  const onPrev = useCallback(() => {
    dispatch(setAiWidgetMapIndex({ chatJid, index: index - 1 }))
  }, [dispatch, chatJid, index])

  const onNext = useCallback(() => {
    dispatch(setAiWidgetMapIndex({ chatJid, index: index + 1 }))
  }, [dispatch, chatJid, index])

  const onClose = useCallback(() => {
    dispatch(clearAiWidgetMaps({ chatJid }))
  }, [dispatch, chatJid])

  const title =
    activeMap?.title || (count > 1 ? t("ai.map.multiple") : t("ai.map.single"))

  return (
    <div className="flex h-full w-full flex-col gap-3 bg-background p-3">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-md">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <h2 className="min-w-0 truncate text-base font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h2>
          <div className="flex shrink-0 items-center gap-1">
            {count > 1 && (
              <>
                <button
                  type="button"
                  onClick={onPrev}
                  disabled={index === 0}
                  className="rounded p-1 text-foreground transition-colors hover:bg-accent disabled:opacity-40"
                  aria-label={t("ai.map.previous")}
                >
                  <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <span className="text-xs text-muted-foreground">
                  {index + 1} / {count}
                </span>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={index === count - 1}
                  className="rounded p-1 text-foreground transition-colors hover:bg-accent disabled:opacity-40"
                  aria-label={t("ai.map.next")}
                >
                  <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-foreground transition-colors hover:bg-accent"
              aria-label={t("ai.map.close")}
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden border-t border-gray-100 dark:border-gray-800">
          <AgentMapPanel chatJid={chatJid} hideChrome />
        </div>
      </div>
    </div>
  )
}

export default MapWidget
